import type { ModalSizeBounds } from "./protocol.js"
import { MODAL_SIZE_MAX_PX } from "./protocol.js"

/**
 * The one set of rules for `livespace-plugin:modal-size`, shared by both ends.
 *
 * The SDK runs it before posting, so an author is told locally what was wrong;
 * the host runs it again on arrival, because the payload comes from a
 * third-party iframe and nothing it sends is used verbatim. Built field by
 * field, never spread, so a foreign key cannot ride along.
 */
export type SanitizedModalSize = {
	bounds: ModalSizeBounds
	/** The keys that were present but unusable — for the SDK's warning. */
	dropped: (keyof ModalSizeBounds)[]
}

const AXES = [
	["minWidthPx", "maxWidthPx"],
	["minHeightPx", "maxHeightPx"],
] as const

const isUsable = (value: unknown): value is number =>
	typeof value === "number" &&
	Number.isFinite(value) &&
	value >= 1 &&
	value <= MODAL_SIZE_MAX_PX

export const sanitizeModalSize = (raw: unknown): SanitizedModalSize => {
	const result: SanitizedModalSize = { bounds: {}, dropped: [] }
	if (!raw || typeof raw !== "object") {
		return result
	}
	const input = raw as Record<string, unknown>

	for (const [minKey, maxKey] of AXES) {
		const min = input[minKey]
		const max = input[maxKey]
		const minOk = isUsable(min)
		const maxOk = isUsable(max)

		if (minKey in input && !minOk) {
			result.dropped.push(minKey)
		}
		if (maxKey in input && !maxOk) {
			result.dropped.push(maxKey)
		}

		// A minimum above the maximum: neither can be trusted over the other, so
		// the whole axis falls back to the host's default.
		if (minOk && maxOk && min > max) {
			result.dropped.push(minKey, maxKey)
			continue
		}
		if (minOk) {
			result.bounds[minKey] = min
		}
		if (maxOk) {
			result.bounds[maxKey] = max
		}
	}

	return result
}
